import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import SectionRenderer from '../components/sections/SectionRenderer';
import './About.css';

const About = () => {
  const [pageData, setPageData] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAboutData();
  }, []);

  const loadAboutData = async () => {
    try {
      setLoading(true);
      
      // Load page data
      const pageResponse = await apiService.getPage('about');
      const raw = pageResponse.data;
      const data = raw?.data || (raw?.success === false ? null : (raw?.id ? raw : null));
      console.log('[ABOUT] Raw API response:', raw);
      console.log('[ABOUT] Parsed page data:', data);
      setPageData(data);
      
      // Load sections
      try {
        const sectionsResponse = await apiService.getPageSections('about');
        const sectionsData = sectionsResponse.data || [];
        console.log('[ABOUT] Sections API Response:', sectionsResponse);
        console.log('[ABOUT] Sections loaded:', sectionsData);
        console.log('[ABOUT] Number of sections:', sectionsData.length);
        if (sectionsData.length > 0) {
          console.log('[ABOUT] First section:', sectionsData[0]);
        }
        setSections(sectionsData);
      } catch (sectionsErr) {
        console.error('[ABOUT] Failed to load sections:', sectionsErr);
        console.error('[ABOUT] Sections error details:', sectionsErr.response?.data);
        // Don't fail the whole page if sections fail
        setSections([]);
      }
      
      setError(null);
    } catch (err) {
      console.error('[ABOUT] API failed:', err.response?.status, err.response?.data, err.message);
      setError('API error: ' + (err.response?.status || '') + ' ' + (err.message || 'unreachable'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="about-page">
        <div style={{background:'#f8d7da', border:'1px solid #f5c2c7', padding:'12px 20px', fontSize:'13px', color:'#842029'}}>
          <strong>⚠️ Database API Error:</strong> {error}
        </div>
        <section className="page-hero">
          <div className="container">
            <h1 className="page-hero-title">About Sampoorna</h1>
            <p className="page-hero-subtitle">Complete, whole and fulfilled</p>
          </div>
        </section>
        <section className="about-content">
          <div className="container">
            <div className="row">
              <div className="col-lg-10 mx-auto">
                <div className="about-section mb-5">
                  <h2 className="mb-4">About Sampoorna</h2>
                  <p>The word Sampoorna means complete, whole and fulfilled. Sampoorna is a wellness, healing and spiritual centre created to help individuals reconnect with themselves—physically, emotionally, mentally and spiritually.</p>
                  <p>True wellness is not merely the absence of illness. It is a state of balance, awareness and harmony within.</p>
                </div>
                <div className="text-center mt-4">
                  <p className="text-muted small"><strong>Note:</strong> Showing static content. Add content via admin to customize.</p>
                  <button className="btn btn-primary me-2" onClick={loadAboutData}>Retry Loading</button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Parse content if it's structured (JSON) or use as HTML
  let parsedContent = null;
  try {
    if (pageData?.content) {
      parsedContent = JSON.parse(pageData.content);
    }
  } catch {
    // Content is regular HTML/text
  }

  return (
    <div className="about-page">
      {/* Render sections if available */}
      {sections.length > 0 ? (
        <>
          {/* Render all sections dynamically */}
          {sections.map((section, index) => (
            <SectionRenderer key={section.id || `section-${index}`} section={section} />
          ))}
        </>
      ) : (
        <>
          {/* Fallback: Show page hero if no sections */}
          <section className="page-hero">
            <div className="container">
              <h1 className="page-hero-title">{pageData?.title || pageData?.page_name || 'About Sampoorna'}</h1>
              <p className="page-hero-subtitle">{pageData?.excerpt || pageData?.meta_description || 'Complete, whole and fulfilled'}</p>
            </div>
          </section>

          {/* Dynamic Content Section */}
          <section className="about-content">
            <div className="container">
          {pageData?.content ? (
            <div className="row">
              <div className="col-lg-10 mx-auto">
                {parsedContent ? (
                  // Structured JSON content
                  <div>
                    {/* Mission Section */}
                    {parsedContent.mission && (
                      <div className="about-section mb-5">
                        <h2 className="mb-4">Our Mission</h2>
                        <p className="lead">{parsedContent.mission}</p>
                      </div>
                    )}
                    
                    {/* Vision Section */}
                    {parsedContent.vision && (
                      <div className="about-section mb-5">
                        <h2 className="mb-4">Our Vision</h2>
                        <p className="lead">{parsedContent.vision}</p>
                      </div>
                    )}
                    
                    {/* About Section */}
                    {parsedContent.about && (
                      <div className="about-section mb-5">
                        <h2 className="mb-4">About Us</h2>
                        <div>
                          {typeof parsedContent.about === 'string' ? (
                            <div dangerouslySetInnerHTML={{ __html: parsedContent.about }} />
                          ) : (
                            parsedContent.about.map((paragraph, index) => (
                              <p key={index} className="mb-3">{paragraph}</p>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Team Section */}
                    {parsedContent.team && (
                      <div className="about-section mb-5">
                        <h2 className="mb-4">Our Team</h2>
                        <div className="row g-4">
                          {parsedContent.team.map((member, index) => (
                            <div key={index} className="col-md-6 col-lg-4">
                              <div className="card team-card">
                                {member.image && (
                                  <img src={member.image} className="card-img-top" alt={member.name} />
                                )}
                                <div className="card-body text-center">
                                  <h5 className="card-title">{member.name}</h5>
                                  {member.title && <h6 className="card-subtitle mb-2 text-muted">{member.title}</h6>}
                                  <p className="card-text">{member.description}</p>
                                  {member.qualifications && (
                                    <ul className="list-unstyled small">
                                      {member.qualifications.map((qual, qIndex) => (
                                        <li key={qIndex}>✓ {qual}</li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Values Section */}
                    {parsedContent.values && (
                      <div className="about-section mb-5">
                        <h2 className="mb-4">Our Values</h2>
                        <div className="row g-4">
                          {parsedContent.values.map((value, index) => (
                            <div key={index} className="col-md-6 col-lg-4">
                              <div className="card value-card h-100">
                                <div className="card-body text-center">
                                  {value.icon && <div className="mb-3" style={{fontSize: '2rem'}}>{value.icon}</div>}
                                  <h5 className="card-title">{value.title}</h5>
                                  <p className="card-text">{value.description}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Services Section */}
                    {parsedContent.services && (
                      <div className="about-section mb-5">
                        <h2 className="mb-4">What We Offer</h2>
                        <div className="row g-4">
                          {parsedContent.services.map((service, index) => (
                            <div key={index} className="col-md-6">
                              <div className="d-flex">
                                {service.icon && (
                                  <div className="flex-shrink-0 me-3">
                                    <div style={{fontSize: '1.5rem'}}>{service.icon}</div>
                                  </div>
                                )}
                                <div>
                                  <h5>{service.title}</h5>
                                  <p>{service.description}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // HTML/Text content - split by paragraphs and format nicely
                  <div className="about-section">
                    {pageData.content.split('\n\n').map((paragraph, index) => (
                      <div key={index} className="mb-4">
                        {paragraph.includes('<') ? (
                          // Contains HTML tags
                          <div dangerouslySetInnerHTML={{ __html: paragraph }} />
                        ) : (
                          // Plain text
                          <p className="lead">{paragraph}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Fallback content when no database content
            <div className="row">
              <div className="col-lg-10 mx-auto">
                <div className="about-section mb-5">
                  <h2 className="mb-4">About Sampoorna</h2>
                  <p>
                    The word Sampoorna means complete, whole and fulfilled. Sampoorna was envisioned as a space where wellness is looked at from a holistic perspective rather than through a single dimension.
                  </p>
                  <p>
                    We believe that the body, mind, emotions, relationships, environment and spiritual self are interconnected. Our aim is to provide guidance, tools and experiences that encourage you to understand yourself better and consciously create greater balance in your life.
                  </p>
                </div>

                <div className="about-section mb-5">
                  <h2 className="mb-4">Our Founder</h2>
                  <div className="doctor-profile">
                    <div className="doctor-info">
                      <p>
                        Dr. Anchal Rana Aggarwal is a dental surgeon, Vedic wellness coach, spiritual coach, astrologer, numerologist, Vastu consultant, meditation and pranayama teacher, and author.
                      </p>
                      <p>
                        Through Sampoorna, her vision is to create an integrated platform where individuals can explore different dimensions of wellbeing and self-awareness.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="about-section">
                  <h2 className="mb-4">Our Approach</h2>
                  <div className="why-grid">
                    <div className="why-card">
                      <div className="why-icon">🌿</div>
                      <h3>Holistic</h3>
                      <p>Wellness is multidimensional. The body, mind, emotions, environment and spirit are held together.</p>
                    </div>
                    <div className="why-card">
                      <div className="why-icon">✨</div>
                      <h3>Personalised</h3>
                      <p>There is no single path that works for everyone. Guidance begins with the individual.</p>
                    </div>
                    <div className="why-card">
                      <div className="why-icon">🕊️</div>
                      <h3>Conscious</h3>
                      <p>Our role is to offer knowledge, tools and a safe space. Your journey remains your own.</p>
                    </div>
                  </div>
                </div>
                
                <div className="text-center mt-5">
                  <p className="text-muted small">
                    <strong>Note:</strong> Add detailed content via admin dashboard for full customization.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
        </>
      )}
    </div>
  );
};

export default About;
